// Automatic scroll reveals for every page inside <main>, including content that
// lazy-loads later. Elements are tagged with data-fx-reveal="<variant>" before
// they are first painted (MutationObserver → rAF), then revealed with a stagger
// when they enter the viewport. Styling lives in index.css (.fx-* section).
import { useEffect } from "react";

const TARGETS = [
  "h1", "h2", "h3", "p", "li", "img", "video", "form", "table", "blockquote",
  "[class*='rounded-2xl']", "[class*='rounded-3xl']",
].map((s) => `main ${s}`).join(",");

// Hero media, dialogs, carousels and things that already animate themselves.
const SKIP = [
  "[data-hero3d]", "[data-fx-skip]", "[role='dialog']", "nav",
  ".stagger-card-animate", ".stagger-card-reset", "[class*='animate-']",
  ".embla", "[aria-roledescription='carousel']", "[class*='fixed']", "[class*='sticky']",
].join(",");

const variantFor = (el: Element) => {
  const tag = el.tagName;
  if (tag === "H1" || tag === "H2") return "mask";
  if (tag === "IMG" || tag === "VIDEO") return "curtain";
  if (tag === "FORM" || tag === "TABLE") return "rise";
  if (/rounded-(2|3)xl/.test(el.getAttribute("class") || "")) return "tilt";
  return "rise";
};

export function useAutoReveal(enabled: boolean, routeKey: string) {
  useEffect(() => {
    const main = document.querySelector("main");
    if (!enabled || !main) return;

    let batch: HTMLElement[] = [];
    let flushQueued = false;
    const timers: number[] = [];

    const finish = (el: HTMLElement) => {
      el.setAttribute("data-fx-done", "");
      el.style.removeProperty("--fx-delay");
    };

    const flush = () => {
      flushQueued = false;
      batch
        .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)
        .forEach((el, i) => {
          const delay = Math.min(i, 7) * 70;
          el.style.setProperty("--fx-delay", `${delay}ms`);
          el.setAttribute("data-fx-in", "");
          timers.push(window.setTimeout(() => finish(el), delay + 1400));
        });
      batch = [];
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          io.unobserve(entry.target);
          batch.push(entry.target as HTMLElement);
        }
        if (batch.length && !flushQueued) {
          flushQueued = true;
          requestAnimationFrame(flush);
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -6% 0px" },
    );

    const scan = () => {
      main.querySelectorAll<HTMLElement>(TARGETS).forEach((el) => {
        if (el.hasAttribute("data-fx")) return;
        el.setAttribute("data-fx", "");
        if (el.closest(SKIP) || el.parentElement?.closest("[data-fx-reveal]")) return;
        // Tiny or invisible things (icons, tracking pixels) aren't worth it.
        if (el.offsetWidth < 24 && el.offsetHeight < 24) return;
        el.setAttribute("data-fx-reveal", variantFor(el));
        io.observe(el);
      });
    };

    let scanQueued = false;
    const mo = new MutationObserver(() => {
      if (scanQueued) return;
      scanQueued = true;
      requestAnimationFrame(() => {
        scanQueued = false;
        scan();
      });
    });
    mo.observe(main, { childList: true, subtree: true });
    scan();

    return () => {
      mo.disconnect();
      io.disconnect();
      timers.forEach(clearTimeout);
      // Never leave content hidden if we unmount mid-reveal.
      main.querySelectorAll<HTMLElement>("[data-fx-reveal]:not([data-fx-in])").forEach((el) => {
        el.setAttribute("data-fx-in", "");
        finish(el);
      });
    };
  }, [enabled, routeKey]);
}
